/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateX,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ItemIDProp } from 'componentregistry';
import TreePopup from 'components/Popup/TreePopup';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import { treeViewFilterAtom } from 'layout/Sidebar/atoms';
import {
  itemIdPickerOpenAtom,
  itemIdPickerPathAtom,
} from 'layout/Sidebar/panels/TreeView/state';
import { PlatformContext } from 'platform/PlatformProvider';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from 'react';
import { isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  icon: {
    color: theme.palette.primary.main,
  },
}));

function ItemIDProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  onChange,
  pageId,
  property,
  value: itemIdValue = '',
  isViewOnlyUser = false,
}: PropertyEditorProps<ItemIDProp, string>) {
  const classes = useStyles();
  const platform = useContext(PlatformContext);
  const setFilter = useStateXAtomSetter(treeViewFilterAtom, {
    params: { treename: 'treepopup' },
  });
  // do not use the designer path, instead use some transient path
  // as this could be used in UpdateProp Action in which case we need to
  // call onChange to change the property and not update the designer path directly
  const path = ['transient', '_itemId_', activeItemId, property];
  const [itemId, setItemId] = useStateX<string>(path, '');
  const [value, setValue] = useState(itemIdValue);
  const [errorMessage, setErrorMessage] = useState('');
  const [open, setOpen] = useStateXAtom(itemIdPickerOpenAtom);
  const itemIdPickerPathSetter = useStateXAtomSetter(itemIdPickerPathAtom);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  useEffect(() => {
    // reset the value in the trasient path
    setItemId('');
  }, [setItemId]);

  const isValid = useCallback(
    (val: string): boolean => {
      if (val) {
        const item = platform.getItem(appUid, pageId, val);
        if (!item?.type) {
          setErrorMessage(`Invalid Item ID [${val}]!`);
          return false;
        }
        if (def.validateItemId) {
          const msg = def.validateItemId(val, {
            appUid,
            pageId,
            itemId: activeItemId,
            mgr,
            platform,
          });
          if (msg) {
            setErrorMessage(msg);
            return false;
          }
        }
        setErrorMessage('');
        return true;
      }
      setErrorMessage('');
      return true;
    },
    [activeItemId, appUid, def, mgr, pageId, platform],
  );

  useEffect(() => {
    if (itemId !== '') {
      // user have selected some item in the popup
      if (isValid(itemId)) {
        onChange(property, itemId);
      }
      setValue(itemId);
    } else {
      setErrorMessage('');
    }
  }, [isValid, itemId, onChange, property, setValue]);

  useEffect(() => {
    // initial validation
    isValid(itemIdValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const id = useId();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (val === '' || isValid(val)) {
      onChange(property, val);
      setErrorMessage('');
    }
    setValue(e.target.value);
  }

  function onBlur() {
    if (itemIdValue !== value) {
      if (isValid(value)) {
        onChange(property, value);
      } else {
        onChange(property, '');
        platform.showError(errorMessage);
      }
    }
  }

  function onClick() {
    setItemId('');
    setOpen(!open);
    itemIdPickerPathSetter(path);
    setFilter(value || def.initialFilter || '');
  }

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <>
      <FormControl
        disabled={isViewOnlyUser}
        data-testid={property}
        className={classes.root}
        classes={{ root: classes.input }}
        fullWidth
        variant="standard"
        required={required}
      >
        <InputLabel htmlFor={id} error={!!errorMessage}>
          {def.label}
        </InputLabel>
        <Input
          disabled={isViewOnlyUser}
          id={id}
          spellCheck={false}
          autoCorrect="off"
          fullWidth
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={onBlur}
          type="text"
          value={value ?? ''}
          onChange={handleChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton disabled={isViewOnlyUser} onClick={onClick}>
                <Icon
                  disabled={isViewOnlyUser}
                  iconName="search"
                  className={classes.icon}
                  data-tip="Pick Item ID"
                />
              </IconButton>
              {helpText ? (
                <Icon
                  // className={classes.tip}
                  iconName="comment-exclamation"
                  data-tip={helpText}
                  data-tip-delay={0}
                  data-tip-at="top"
                />
              ) : null}
            </InputAdornment>
          }
        />
        <FormHelperText error>{errorMessage}</FormHelperText>
      </FormControl>
      <TreePopup appUid={appUid} pageId={pageId} />
    </>
  );
}

export default memo(ItemIDProperty);
